import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import AuroraMinFlowLimitControl from './AuroraMinFlowLimitControl'
import { AuroraAssetGroup, AuroraSetMinFlowLimitActionInput } from '../types'
import { publishAuroraAwhpAction } from '../services/aurora'
import { createErrorNotification, createSuccessNotification } from '../redux/slices/notifications'
import { useAuroraStack } from '../hooks'

export interface AuroraAssetGroupMinFlowLimitControlProps {
  auroraAssetGroup: AuroraAssetGroup
  beforeSubmit?: (input: AuroraSetMinFlowLimitActionInput) => void
  afterSubmit?: (value: number) => void
  onError?: (error: unknown) => void
}

export default function AuroraAssetGroupMinFlowLimitControl({
  auroraAssetGroup,
  beforeSubmit,
  afterSubmit,
  onError
}: AuroraAssetGroupMinFlowLimitControlProps) {
  const { t } = useTranslation('aurora')

  const dispatch = useDispatch()

  const {
    auroras,
    minFlowLimit: initialMinFlowLimit,
    handleAuroraAwhpActionError
  } = useAuroraStack(auroraAssetGroup)

  const [isSubmitting, setSubmitting] = useState<boolean>(false)

  const [minMinFlowLimit, setMinFlowLimit] = useState<number | null>(initialMinFlowLimit)

  async function handleSubmit(input: AuroraSetMinFlowLimitActionInput) {
    try {
      setSubmitting(true)

      beforeSubmit?.(input)

      await Promise.all(auroras.map((aurora) => publishAuroraAwhpAction(aurora.id, input)))

      dispatch(createSuccessNotification(t('setMinFlowLimitSuccess')))
      setMinFlowLimit(input.alarms.minFlowLimit)
      afterSubmit?.(input.alarms.minFlowLimit)
    } catch (error) {
      onError?.(error)
      handleAuroraAwhpActionError(error, t('setMinFlowLimitError'))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <AuroraMinFlowLimitControl
      value={minMinFlowLimit}
      loading={isSubmitting}
      onSubmit={handleSubmit}
    />
  )
}
